import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Lock, FileText, MessageSquare,
  Stethoscope, History, Receipt, Pencil, Trash2, ClipboardList,
} from "lucide-react";
import { useAuth } from "../AuthContext";
import api, { apiError, fmtDate, fmtDateTime, downloadBlob, calcAge } from "../api";
import { Card, Button, Input, Textarea, Field, StatusBadge, Badge, Spinner, STATUS_LABELS } from "../components/ui";
import DocUploadSlot from "../components/DocUploadSlot";

const CATEGORIES = [
  { key: "declaration", label: "Candidate Declaration Form", hint: "PDF only", accept: ".pdf" },
  { key: "ilo_page", label: "ILO Page", hint: "PDF or Image (JPG/PNG)", accept: ".pdf,.jpg,.jpeg,.png" },
  { key: "medical_reports", label: "Medical Reports", hint: "PDF only", accept: ".pdf" },
  { key: "consent_form", label: "MedVerify Consent Form", hint: "PDF only", accept: ".pdf" },
  { key: "additional_tests", label: "Prescription / Additional Tests", hint: "PDF or Image (JPG/PNG)", accept: ".pdf,.jpg,.jpeg,.png" },
];

export default function CandidateDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cand, setCand] = useState(null);
  const [error, setError] = useState("");
  const [verdict, setVerdict] = useState("");
  const [remarks, setRemarks] = useState("");
  const [repeatTests, setRepeatTests] = useState("");
  const [newRemark, setNewRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const [cancelMode, setCancelMode] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [signoffText, setSignoffText] = useState("");
  const [medHistText, setMedHistText] = useState("");
  const [portDoctorText, setPortDoctorText] = useState("");

  const load = useCallback(() => {
    api.get(`/candidates/${id}`)
      .then((r) => setCand(r.data))
      .catch((e) => setError(apiError(e)));
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  if (error) return <p className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</p>;
  if (!cand) return <div className="flex justify-center py-20"><Spinner /></div>;

  const canReview = user.role === "doctor";
  const REQUIRED_FOR_VERDICT = ["declaration", "ilo_page", "medical_reports", "consent_form"];
  const missingForVerdict = cand
    ? CATEGORIES.filter((c) => REQUIRED_FOR_VERDICT.includes(c.key) && !(cand.documents || []).some((d) => d.category === c.key && !d.superseded)).map((c) => c.label)
    : [];
  const alreadyReviewed = cand.status !== "pending";
  const canEdit = user.role === "admin";
  const canCancelReg = (user.role === "admin" || (user.role === "clinic" && cand.clinic_org_id === user.organization_id)) &&
    cand.status === "pending" && !cand.is_final;
  const isReturningCase = (cand.history || []).length > 0;

  const cancelRegistration = async () => {
    try {
      const { data } = await api.delete(`/candidates/${id}/registration`);
      if (data.removed === "visit") {
        toast.success("Visit cancelled — case restored to its previous state");
        setCancelMode(false);
        setCancelConfirm(false);
        load();
      } else {
        toast.success("Registration cancelled — entry removed");
        navigate("/candidates");
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const canUploadCat = (key) => {
    if (user.role === "admin") return true;
    if (user.role !== "clinic") return false;
    if (cand.status === "temp_unfit") return true;
    if (cand.is_final) return false;
    if (key === "additional_tests") return true;
    const active = cand.documents.filter((d) => d.category === key && !d.superseded);
    return active.length === 0 || cand.reupload_open;
  };

  const submitReview = async () => {
    if (!verdict) return toast.error("Select a verdict");
    setBusy(true);
    try {
      const { data } = await api.post(`/candidates/${id}/review`, { verdict, remarks, repeat_tests: repeatTests });
      toast.success(`Marked ${STATUS_LABELS[verdict]}${data.email_sent ? " — client notified by email" : ""}`);
      setVerdict(""); setRemarks(""); setRepeatTests("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const addRemark = async () => {
    if (!newRemark.trim()) return;
    setBusy(true);
    try {
      await api.post(`/candidates/${id}/remarks`, { text: newRemark });
      setNewRemark("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const addSignoff = async () => {
    if (!signoffText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/candidates/${id}/signoff-notes`, { text: signoffText.trim() });
      setSignoffText("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const addMedHist = async () => {
    if (!medHistText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/candidates/${id}/medical-history-notes`, { text: medHistText.trim() });
      setMedHistText("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const addPortDoctorNote = async () => {
    if (!portDoctorText.trim()) return;
    setBusy(true);
    try {
      await api.post(`/candidates/${id}/port-doctor-notes`, { text: portDoctorText.trim() });
      setPortDoctorText("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/admin/documents/${docId}`);
      toast.success("Document deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteCandidate = async () => {
    if (!window.confirm(`Delete candidate ${cand.name}? All their documents, remarks and history will be removed permanently. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/candidates/${id}`);
      toast.success("Candidate deleted");
      navigate("/candidates");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-5xl">
      <Link to="/candidates" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-navy">
        <ArrowLeft size={15} /> Back to candidates
      </Link>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-3xl font-extrabold text-slate-900" data-testid="candidate-detail-name">{cand.name}</h1>
        <StatusBadge status={cand.status} />
        {cand.is_final && (
          <Badge color="navy" className="gap-1" data-testid="final-locked-badge"><Lock size={11} /> Final — Locked</Badge>
        )}
        {cand.needs_review && !cand.is_final && <Badge color="teal">Awaiting Doctor Review</Badge>}
        {cand.reupload_open && !cand.is_final && <Badge color="amber" data-testid="reupload-open-badge">Re-upload Open</Badge>}
        <div className="ml-auto flex gap-2">
          {user.role !== "pandi" && (
            <Button size="sm" variant="outline" onClick={() => navigate(`/candidates/${id}/history`)} data-testid="prev-history-btn">
              <History size={14} /> Previous History
            </Button>
          )}
          {canEdit && (
            <Button size="sm" variant="outline" onClick={() => navigate(`/candidates/${id}/edit`)} data-testid="edit-candidate-btn">
              <Pencil size={14} /> Edit
            </Button>
          )}
          {["admin", "accounts"].includes(user.role) && (
            <Button size="sm" variant="outline" onClick={() => downloadBlob(`/billing/invoice/${id}`, `invoice_${cand.name}.pdf`)} data-testid="download-invoice-btn">
              <Receipt size={14} /> Invoice
            </Button>
          )}
          {user.role === "admin" && (
            <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={deleteCandidate} data-testid="delete-candidate-btn">
              <Trash2 size={14} /> Delete
            </Button>
          )}
          {canCancelReg && (
            <Button size="sm" variant="danger" onClick={() => setCancelMode(true)} data-testid="cancel-registration-btn">
              Cancel Registration (For Wrong Entry or Incorrect Document Upload)
            </Button>
          )}
        </div>
      </div>

      {canCancelReg && cancelMode && (
        <div className="mb-6 space-y-3 rounded-md border border-red-200 bg-red-50 p-4 fade-up" data-testid="cancel-registration-box">
          <label className="flex cursor-pointer items-start gap-2 text-sm text-red-700">
            <input type="checkbox" checked={cancelConfirm} onChange={(e) => setCancelConfirm(e.target.checked)} className="mt-0.5 accent-red-600" data-testid="cancel-confirm-checkbox" />
            {isReturningCase
              ? "I confirm this visit should be cancelled. Documents and details of this visit will be removed and the case will return to its previous verdict. Previous history is preserved."
              : "I confirm this entry was filled in wrong and should be permanently removed. It will not be stored in the system."}
          </label>
          <div className="flex gap-2">
            <Button type="button" variant="danger" size="sm" disabled={!cancelConfirm} onClick={cancelRegistration} data-testid="confirm-cancel-registration-btn">{isReturningCase ? "Cancel This Visit" : "Remove Entry"}</Button>
            <Button type="button" variant="outline" size="sm" onClick={() => { setCancelMode(false); setCancelConfirm(false); }} data-testid="keep-registration-btn">Keep</Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {/* info */}
          <Card className="p-6">
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm sm:grid-cols-3">
              {[
                ["Company ID", cand.company_id], ["Rank", cand.rank],
                ["Date of Birth", `${fmtDate(cand.dob)}${calcAge(cand.dob) !== null ? ` (${calcAge(cand.dob)} yrs)` : ""}`],
                ["Mobile", cand.mobile], ["INDOS No.", cand.indos_number || "-"], ["CDC No.", cand.cdc_number || "-"],
                ["Clinic", cand.clinic_name], ["Client", cand.client_name], ["Registered", fmtDate(cand.created_at)],
                ["P&I Club", cand.pandi_name || "-"], ["Vessel", cand.vessel || "-"],
                ["Verdict By", cand.reviewed_by && cand.status !== "pending" ? cand.reviewed_by : "-"],
              ].map(([k, v]) => (
                <div key={k}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">{k}</p>
                  <p className="mt-0.5 font-medium text-slate-800">{v}</p>
                </div>
              ))}
            </div>
            {cand.services.length > 0 && (
              <div className="mt-5 border-t border-slate-100 pt-4">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Services</p>
                <div className="flex flex-wrap gap-3">
                  {cand.services.map((s) => (
                    <div key={s.id}>
                      <Badge color="slate">
                        {s.name}{s.cost !== undefined ? ` · ₹${s.cost}` : ""}
                      </Badge>
                      {s.description && <p className="mt-1 max-w-xs text-xs text-slate-500" data-testid={`service-desc-${s.id}`}>{s.description}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* documents — hidden for P&I role */}
          {user.role !== "pandi" && (
          <Card>
            <div className="flex items-center gap-2 border-b border-slate-200 px-6 py-4">
              <FileText size={17} className="text-navy" />
              <h3 className="font-heading text-lg font-bold text-slate-900">Documents</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {(user.role === "client" ? CATEGORIES.filter((c) => cand.documents.some((d) => d.category === c.key)) : CATEGORIES).map((c) => (
                <div key={c.key} className="px-6 py-5">
                  <DocUploadSlot
                    candidateId={id}
                    category={c.key}
                    label={c.label}
                    hint={c.hint}
                    accept={c.accept}
                    docs={cand.documents.filter((d) => d.category === c.key)}
                    canUpload={["clinic", "admin"].includes(user.role) && canUploadCat(c.key)}
                    isAdmin={user.role === "admin"}
                    onChanged={load}
                    onDelete={deleteDoc}
                  />
                </div>
              ))}
            </div>
          </Card>
          )}

          {/* remarks thread — hidden for client & P&I roles */}
          {!["client", "pandi"].includes(user.role) && (
          <Card>
            <div className="flex items-center gap-2 border-b border-slate-200 px-6 py-4">
              <MessageSquare size={17} className="text-navy" />
              <h3 className="font-heading text-lg font-bold text-slate-900">Chatbox</h3>
            </div>
            <div className="space-y-3 p-6">
              {cand.remarks.length === 0 && <p className="text-sm text-slate-400">No remarks yet.</p>}
              {cand.remarks.map((r) => (
                <div key={r.id} className={`rounded-lg border p-4 ${r.author_role === "doctor" ? "border-teal/40 bg-teal-light/30" : "border-slate-200 bg-slate-50"}`} data-testid="remark-item">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold text-slate-900">{r.author_name}</span>
                    <Badge color={r.author_role === "doctor" ? "teal" : "slate"}>{r.author_role}</Badge>
                    {r.verdict && <StatusBadge status={r.verdict} />}
                    <span className="ml-auto text-xs text-slate-400">{fmtDateTime(r.created_at)}</span>
                  </div>
                  {r.text && <p className="text-sm text-slate-700">{r.text}</p>}
                  {r.repeat_tests && (
                    <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800">
                      Required repeat tests: {r.repeat_tests}
                    </p>
                  )}
                </div>
              ))}
              {["clinic", "doctor", "admin"].includes(user.role) && (
                <div className="flex gap-2 border-t border-slate-100 pt-4">
                  <Input
                    placeholder={user.role === "clinic" ? "Reply to doctor remarks..." : "Add a remark..."}
                    value={newRemark}
                    onChange={(e) => setNewRemark(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addRemark()}
                    data-testid="remark-input"
                  />
                  <Button onClick={addRemark} disabled={busy || !newRemark.trim()} data-testid="send-remark-btn">Send</Button>
                </div>
              )}
            </div>
          </Card>
          )}

          {/* medical history */}
          <Card data-testid="medical-history-section">
            <div className="flex items-center gap-2 border-b border-slate-200 px-6 py-4">
              <ClipboardList size={17} className="text-navy" />
              <h3 className="font-heading text-lg font-bold text-slate-900">Medical history</h3>
            </div>
            <div className="space-y-3 p-6">
              {(cand.medical_history_notes || []).length === 0 && <p className="text-sm text-slate-400">No medical history notes yet.</p>}
              {(cand.medical_history_notes || []).map((n) => (
                <div key={n.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="medical-history-note">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold text-slate-900">{n.author_name}</span>
                    <Badge color="slate">{n.author_role}</Badge>
                    <span className="ml-auto text-xs text-slate-400">{fmtDateTime(n.created_at)}</span>
                  </div>
                  <p className="text-sm text-slate-700">{n.text}</p>
                </div>
              ))}
              {["clinic", "doctor", "admin"].includes(user.role) && (
                <div className="flex gap-2 border-t border-slate-100 pt-4">
                  <Input
                    placeholder="Add medical history note..."
                    value={medHistText}
                    onChange={(e) => setMedHistText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addMedHist()}
                    data-testid="medical-history-input"
                  />
                  <Button onClick={addMedHist} disabled={busy || !medHistText.trim()} data-testid="send-medical-history-btn">Send</Button>
                </div>
              )}
            </div>
          </Card>

          {/* sign off / P&I case — visible to all roles */}
          <Card data-testid="signoff-section">
            <div className="flex items-center gap-2 border-b border-slate-200 px-6 py-4">
              <ClipboardList size={17} className="text-navy" />
              <h3 className="font-heading text-lg font-bold text-slate-900">Sign off / P&amp;I case</h3>
            </div>
            <div className="space-y-3 p-6">
              {(cand.signoff_notes || []).length === 0 && <p className="text-sm text-slate-400">No sign off / P&amp;I case notes yet.</p>}
              {(cand.signoff_notes || []).map((n) => (
                <div key={n.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="signoff-note">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold text-slate-900">{n.author_name}</span>
                    <Badge color="slate">{n.author_role === "pandi" ? "P&I" : n.author_role}</Badge>
                    <span className="ml-auto text-xs text-slate-400">{fmtDateTime(n.created_at)}</span>
                  </div>
                  <p className="text-sm text-slate-700">{n.text}</p>
                </div>
              ))}
              {["clinic", "doctor", "pandi", "admin"].includes(user.role) && (
                <div className="flex gap-2 border-t border-slate-100 pt-4">
                  <Input
                    placeholder="Add sign off / P&I case note..."
                    value={signoffText}
                    onChange={(e) => setSignoffText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addSignoff()}
                    data-testid="signoff-input"
                  />
                  <Button onClick={addSignoff} disabled={busy || !signoffText.trim()} data-testid="send-signoff-btn">Send</Button>
                </div>
              )}
            </div>
          </Card>

          {/* RMA and Port doctor visit — combined */}
          <Card data-testid="rma-port-doctor-section">
            <div className="flex items-center gap-2 border-b border-slate-200 px-6 py-4">
              <ClipboardList size={17} className="text-navy" />
              <h3 className="font-heading text-lg font-bold text-slate-900">RMA and Port doctor visit</h3>
            </div>
            <div className="space-y-3 p-6">
              {(() => {
                const notes = [
                  ...(cand.rma_comments || []).map((n) => ({ ...n, author_role: "admin" })),
                  ...(cand.port_doctor_notes || []),
                ].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
                return (
                  <>
                    {notes.length === 0 && <p className="text-sm text-slate-400">No notes yet.</p>}
                    {notes.map((n) => (
                      <div key={n.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="rma-port-doctor-note">
                        <div className="mb-1 flex flex-wrap items-center gap-2">
                          <span className="text-sm font-bold text-slate-900">{n.author_name}</span>
                          <Badge color={n.author_role === "doctor" ? "teal" : "navy"}>{n.author_role}</Badge>
                          <span className="ml-auto text-xs text-slate-400">{fmtDateTime(n.created_at)}</span>
                        </div>
                        <p className="text-sm text-slate-700">{n.text}</p>
                      </div>
                    ))}
                  </>
                );
              })()}
              {["clinic", "doctor", "pandi", "admin"].includes(user.role) && (
                <div className="flex gap-2 border-t border-slate-100 pt-4">
                  <Input
                    placeholder="Add note..."
                    value={portDoctorText}
                    onChange={(e) => setPortDoctorText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addPortDoctorNote()}
                    data-testid="rma-port-doctor-input"
                  />
                  <Button onClick={addPortDoctorNote} disabled={busy || !portDoctorText.trim()} data-testid="send-rma-port-doctor-btn">Send</Button>
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          {/* doctor review */}
          {canReview && (
            <Card className="border-teal/50 p-6" data-testid="doctor-review-panel">
              <div className="mb-4 flex items-center gap-2">
                <Stethoscope size={17} className="text-teal" />
                <h3 className="font-heading text-lg font-bold text-slate-900">{alreadyReviewed ? "Change Verdict" : "Doctor Review"}</h3>
              </div>
              {alreadyReviewed && (
                <p className="mb-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="change-verdict-note">
                  Current verdict: <strong>{STATUS_LABELS[cand.status] || cand.status}</strong>. Submitting a new verdict will replace it — every change is recorded in history.
                </p>
              )}
              <div className="space-y-4">
                <Field label="Verdict" required>
                  <div className="grid grid-cols-1 gap-2">
                    {[["fit", "Accepted"], ["temp_unfit", "Further Evaluation Required"], ["unfit", "Not Recommended"]].map(([v, label]) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setVerdict(v)}
className={`min-h-[84px] rounded-md border px-3 py-3 text-sm font-semibold leading-snug text-center whitespace-normal break-words transition-colors ${                          verdict === v
                            ? v === "fit" ? "border-emerald-600 bg-emerald-600 text-white"
                              : v === "temp_unfit" ? "border-amber-500 bg-amber-500 text-white"
                              : "border-red-600 bg-red-600 text-white"
                            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                        data-testid={`verdict-${v}-btn`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Remarks">
                  <Textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Only Verdict Specific Remarks to be entered" data-testid="review-remarks-input" />
                </Field>
                <Field label="Required Repeat Tests">
                  <Input value={repeatTests} onChange={(e) => setRepeatTests(e.target.value)} placeholder="e.g. Repeat ECG, Chest X-Ray" data-testid="repeat-tests-input" />
                </Field>
                {missingForVerdict.length > 0 && (
                  <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="verdict-blocked-note">
                    Verdict locked — missing documents: {missingForVerdict.join(", ")}
                  </p>
                )}
                <Button className="w-full" onClick={submitReview} disabled={busy || missingForVerdict.length > 0} data-testid="submit-review-btn">
                  {alreadyReviewed ? "Update Verdict" : "Submit Verdict"}
                </Button>
              </div>
            </Card>
          )}

          {/* history — visible to all roles */}
          <Card>
            <div className="flex items-center gap-2 border-b border-slate-200 px-6 py-4">
              <History size={17} className="text-navy" />
              <h3 className="font-heading text-lg font-bold text-slate-900">Verdict History</h3>
              {user.role !== "pandi" && (
                <Link to={`/candidates/${id}/history`} className="ml-auto text-xs font-bold text-teal transition-colors hover:text-navy" data-testid="view-full-history-link">
                  Full timeline →
                </Link>
              )}
            </div>
            <div className="p-6">
              {cand.history.length === 0 ? (
                <p className="text-sm text-slate-400">No review history yet.</p>
              ) : (
                <ol className="space-y-4">
                  {cand.history.map((h) => (
                    <li key={h.id} className="relative border-l-2 border-slate-200 pl-4" data-testid="history-item">
                      <span className="absolute -left-[5px] top-1.5 h-2 w-2 rounded-full bg-teal" />
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-500">{fmtDate(h.created_at)}</span>
                        <StatusBadge status={h.status} />
                      </div>
                      <p className="mt-1 text-xs text-slate-500">{h.doctor_name} · {fmtDateTime(h.created_at)}</p>
                      {h.remarks && <p className="mt-1 text-xs text-slate-600">{h.remarks}</p>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </Card>

          {user.role === "doctor" && cand.client_description && (
            <Card data-testid="client-description-card">
              <div className="flex items-center gap-2 border-b border-slate-200 px-6 py-4">
                <FileText size={17} className="text-navy" />
                <h3 className="font-heading text-lg font-bold text-slate-900">{cand.client_name ? `${cand.client_name} SOP` : "Client SOP"}</h3>
              </div>
              <div className="p-6">
                <p className="whitespace-pre-wrap text-sm text-slate-700">{cand.client_description}</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
